import { useState, useRef, useCallback, useMemo } from 'preact/hooks';
import { defaultBreakpoints } from '../UNSAFE_useBreakpointsExclusive';

export type Breakpoints = Record<string, `${number}px`>;
/**
 * This custom hook returns the current breakpoint based on the width of a container, which is a DOM object, "exclusive" meaning only the breakpoint matching the container width will be returned.
 * The breakpoints are returned as a set of boolean values keyed to each breakpoint.
 */
export function useContainerBreakpointsExclusive(breakpoints: Breakpoints = defaultBreakpoints) {
  const [width, setWidth] = useState(0);
  const observedNodeRef = useRef<HTMLElement | null>(null);

  const resizeObserver = useMemo(
    () =>
      new ResizeObserver((entries) => {
        for (const entry of entries) {
          setWidth(entry.contentRect.width);
        }
      }),
    []
  );

  const ref = useCallback(
    (node: HTMLElement) => {
      const observedNode = observedNodeRef.current;

      if (node != observedNode) {
        if (observedNode) {
          resizeObserver.unobserve(observedNode);
        }

        if (node) {
          resizeObserver.observe(node);
        }

        observedNodeRef.current = node;
      }
    },
    [resizeObserver]
  );

  const breakpointMatches = useMemo(() => {
    const matches: Record<string, boolean> = {};
    for (const [k] of Object.entries(breakpoints).reverse()) {
      matches[k] = width >= parseInt(breakpoints[k]);
      if (matches[k]) {
        break; // stop at the first match
      }
    }
    return matches;
  }, [width, breakpoints]);

  return {
    breakpointMatches,
    ref
  };
}
